/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import { FaShieldAlt } from "react-icons/fa";
import { Link, useNavigate } from "react-router-dom";

// Redux
// eslint-disable-next-line no-unused-vars
import { useSelector, useDispatch } from "react-redux";
import {
  useCreateNewOrderMutation,
  useEstimateShippingCostMutation,
} from "../../redux/api/orderApi";
import { clearCart } from "../../redux/features/cartSlice";

import { caluclateOrderCost } from "../../helpers/caluclateOrderCost";

import MetaData from "../../components/layout/MetaData";
import Message from "../../components/Message";
import Loader from "../../components/Loader";
import { toast } from "react-toastify";
import { getCartItemLink, getCartItemKey } from "../../helpers/cartItem";
import {
  buildCartShippingDebugItems,
  buildShippingEstimatePayload,
  extractEstimatedShippingFee,
  extractShippingQuoteOptions,
  formatShippingQuoteOptionLabel,
  formatShippingQuoteSelectedLabel,
  logCountryCodeDebug,
  logShippingEstimateDebug,
} from "../../helpers/shippingEstimate";
import "../Payment/CheckoutPage.css";

/**
 * 确认订单页：展示订单信息并提交创建订单。
 */
const ConfirmOrder = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [showModal, setShowModal] = useState(false);
  const { cartItems, shippingInfo, paymentMethod } = useSelector(
    (state) => state.cart
  );
  const { user } = useSelector((state) => state.auth);
  const {
    itemsPrice: fallbackItemsPrice,
    shippingPrice: fallbackShippingPrice,
    taxPrice: fallbackTaxPrice,
  } = caluclateOrderCost(cartItems);
  const [estimatedShippingPrice, setEstimatedShippingPrice] = useState(null);
  const [shippingPriceSource, setShippingPriceSource] = useState("fallback");
  const [shippingEstimateError, setShippingEstimateError] = useState("");
  const [shippingQuoteOptions, setShippingQuoteOptions] = useState([]);
  const [selectedShippingQuoteId, setSelectedShippingQuoteId] = useState("");
  const [isShippingQuoteSelectExpanded, setIsShippingQuoteSelectExpanded] = useState(false);

  const itemsPrice = Number(fallbackItemsPrice).toFixed(2);
  const selectedShippingQuote = shippingQuoteOptions.find(
    (option) => option.id === selectedShippingQuoteId
  );
  const shippingPrice =
    selectedShippingQuote?.fee ??
    (estimatedShippingPrice === null ? fallbackShippingPrice : estimatedShippingPrice);
  const isShippingEstimateFailed = shippingPriceSource === "failed";
  const totalPrice = (
    Number(itemsPrice) +
    Number(shippingPrice) +
    Number(fallbackTaxPrice)
  ).toFixed(2);
  const itemCount = cartItems.reduce(
    (total, item) => total + Number(item?.quantity || 0),
    0
  );

  const [createNewOrder, { isLoading, error, isSuccess }] =
    useCreateNewOrderMutation();
  const [estimateShippingCost] = useEstimateShippingCostMutation();

  /**
   * 格式化单价显示为 6 位小数，避免因后端返回类型不一致导致渲染异常。
   * @param {number|string} price 商品单价
   * @returns {string} 固定 6 位小数字符串
   */
  const formatUnitPrice = (price) => Number(price || 0).toFixed(6);

  useEffect(() => {
    if (isSuccess) {
      toast.success("Order Placed Successfully");
    }
    if (error) {
      toast.error(error?.data?.message);
    }
  }, [isSuccess, error]);

  useEffect(() => {
    /**
     * 根据购物车与收货国家调用 4PX 运费预估。
     */
    const estimateOrderShipping = async () => {
      logShippingEstimateDebug("ConfirmOrder", "进入运费预估流程", {
        cartItemCount: cartItems?.length || 0,
        rawCountry: shippingInfo?.country,
        fallbackShippingPrice,
        itemsPrice,
      });

      if (!cartItems?.length) {
        logShippingEstimateDebug("ConfirmOrder", "回退到固定运费：购物车为空", {
          fallbackShippingPrice,
        });
        setEstimatedShippingPrice(null);
        setShippingPriceSource("fallback");
        setShippingEstimateError("");
        setShippingQuoteOptions([]);
        setSelectedShippingQuoteId("");
        return;
      }

      const {
        payload: estimatePayload,
        countryCode,
        totalWeightInGrams,
        debugItems,
      } = buildShippingEstimatePayload(cartItems, shippingInfo?.country);
      logCountryCodeDebug("ConfirmOrder", shippingInfo?.country, countryCode);
      logShippingEstimateDebug("ConfirmOrder", "4PX 请求前置参数检查", {
        rawCountry: shippingInfo?.country,
        countryCode,
        totalWeightInGrams,
        cartItems: debugItems || buildCartShippingDebugItems(cartItems),
      });
      if (!countryCode || !estimatePayload) {
        logShippingEstimateDebug("ConfirmOrder", "回退到固定运费：收货国家无法识别为国家码", {
          rawCountry: shippingInfo?.country,
          fallbackShippingPrice,
        });
        setEstimatedShippingPrice(null);
        setShippingPriceSource("failed");
        setShippingEstimateError("Shipping estimate failed / Contact us");
        setShippingQuoteOptions([]);
        setSelectedShippingQuoteId("");
        return;
      }

      logShippingEstimateDebug("ConfirmOrder", "准备请求 4PX 运费预估", {
        payload: estimatePayload,
        rawCountry: shippingInfo?.country,
        fallbackShippingPrice,
        itemsPrice,
        cartItems: debugItems || buildCartShippingDebugItems(cartItems),
      });

      try {
        const estimateResponse = await estimateShippingCost(estimatePayload).unwrap();

        logShippingEstimateDebug("ConfirmOrder", "收到后端 4PX 运费预估响应", {
          response: estimateResponse,
        });

        const quoteOptions = extractShippingQuoteOptions(
          estimateResponse,
          "ConfirmOrder"
        );
        const savedShippingQuoteCode =
          shippingInfo?.selectedShippingOption?.logisticsProductCode;
        const nextSelectedQuote =
          quoteOptions.find(
            (option) => option.logisticsProductCode === savedShippingQuoteCode
          ) || quoteOptions[0];
        const estimatedFee = nextSelectedQuote?.fee ?? extractEstimatedShippingFee(
          estimateResponse,
          "ConfirmOrder"
        );
        if (Number.isFinite(estimatedFee)) {
          logShippingEstimateDebug("ConfirmOrder", "使用 4PX 运费预估金额", {
            estimatedFee,
            selectedShippingQuote: nextSelectedQuote || null,
            quoteOptions,
            fallbackShippingPrice,
          });
          setEstimatedShippingPrice(estimatedFee);
          setShippingQuoteOptions(quoteOptions);
          setSelectedShippingQuoteId(nextSelectedQuote?.id || "");
          setShippingPriceSource("4px");
          setShippingEstimateError("");
          return;
        }

        logShippingEstimateDebug("ConfirmOrder", "回退到固定运费：4PX 响应中未解析到可用费用字段", {
          fallbackShippingPrice,
          response: estimateResponse,
        });
      } catch (estimateError) {
        console.error("[ConfirmOrder] 4PX 运费预估失败，停止显示固定兜底运费:", {
          error: estimateError,
          fallbackShippingPrice,
        });
        setShippingEstimateError("Shipping estimate failed / Contact us");
      }

      logShippingEstimateDebug("ConfirmOrder", "4PX 运费预估失败，停止显示固定兜底运费", {
        fallbackShippingPrice,
      });
      setEstimatedShippingPrice(null);
      setShippingPriceSource("failed");
      setShippingQuoteOptions([]);
      setSelectedShippingQuoteId("");
      setShippingEstimateError((currentError) => (
        currentError || "Shipping estimate failed / Contact us"
      ));
    };

    estimateOrderShipping();
  }, [
    cartItems,
    shippingInfo?.country,
    shippingInfo?.selectedShippingOption?.logisticsProductCode,
    estimateShippingCost,
  ]);

  /**
   * 更新客户在确认页选择的 4PX 物流渠道。
   * @param {React.ChangeEvent<HTMLSelectElement>} event 渠道下拉变更事件
   */
  const handleShippingQuoteChange = (event) => {
    const nextQuoteId = event.target.value;
    const nextQuote = shippingQuoteOptions.find((option) => option.id === nextQuoteId);
    setSelectedShippingQuoteId(nextQuoteId);
    setIsShippingQuoteSelectExpanded(false);
    if (nextQuote) {
      setEstimatedShippingPrice(nextQuote.fee);
      logShippingEstimateDebug("ConfirmOrder", "客户选择 4PX 物流渠道", {
        selectedShippingQuote: nextQuote,
      });
    }
  };

  /**
   * 标记物流渠道下拉进入展开状态，展开时显示完整渠道信息。
   */
  const handleShippingQuoteSelectExpand = () => {
    setIsShippingQuoteSelectExpanded(true);
  };

  /**
   * 标记物流渠道下拉离开展开状态，收起预览只显示价格。
   */
  const handleShippingQuoteSelectCollapse = () => {
    setIsShippingQuoteSelectExpanded(false);
  };

  /**
   * 提交订单：创建成功后清空购物车并跳转订单详情页。
   */
  const submitHandler = async () => {
    if (isShippingEstimateFailed) {
      toast.error(shippingEstimateError || "Shipping estimate failed / Contact us");
      logShippingEstimateDebug("ConfirmOrder", "阻止提交订单：运费预估失败", {
        shippingEstimateError,
      });
      return;
    }

    const {
      itemsPrice: recalculatedItemsPrice,
      shippingPrice: recalculatedShippingPrice,
      taxPrice,
    } = caluclateOrderCost(cartItems);
    const finalShippingPrice =
      estimatedShippingPrice === null
        ? recalculatedShippingPrice
        : estimatedShippingPrice;
    const finalTotalPrice = (
      Number(recalculatedItemsPrice) +
      Number(finalShippingPrice) +
      Number(taxPrice)
    ).toFixed(2);

    // Create WireTransfer Order
    const orderData = {
      orderItems: cartItems,
      shippingInfo,
      paymentMethod: paymentMethod,
      paymentInfo: {
        status: "Not Paid",
      },
      itemsPrice: Number(recalculatedItemsPrice),
      shippingAmount: Number(finalShippingPrice),
      taxAmount: taxPrice,
      totalAmount: Number(finalTotalPrice),
      selectedShippingOption: selectedShippingQuote || shippingInfo?.selectedShippingOption || null,
    };
    const res = await createNewOrder(orderData);
    if (res) {
      dispatch(clearCart());
      navigate(`/me/order/${res?.data?.order?._id}`);
    }
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <MetaData title="Confirm Order" />
      <main className="checkout-reference-page">
        <div className="checkout-reference-container">
          <nav className="checkout-breadcrumb" aria-label="Breadcrumb">
            <Link to="/cart"><i className="fas fa-shopping-cart" aria-hidden="true"></i>Shopping Cart</Link>
            <span>/</span>
            <strong>Confirm Order</strong>
          </nav>

          <div className="checkout-review-bars" aria-label="Checkout progress">
            <div><span>Shipping Address</span><Link to="/shipping">Edit</Link></div>
            <div><span>Payment Method</span><Link to="/payment_method">Edit</Link></div>
          </div>

          <div className="checkout-reference-layout">
            <section className="checkout-main-column">
              <h1>Confirm and Pay</h1>
              <div className="checkout-panel confirm-order-panel">
                <div className="confirm-info-block">
                  <h2>Shipping</h2>
                  <p><strong>Name:</strong> {user?.name}</p>
                  <p><strong>Phone:</strong> {shippingInfo?.phoneNo}</p>
                  <p><strong>Address:</strong> {`${shippingInfo?.address}, ${shippingInfo?.city}, ${shippingInfo?.zipCode}, ${shippingInfo?.country}`}</p>
                </div>

                <div className="confirm-info-block">
                  <h2>Payment Method</h2>
                  <p><strong>Method:</strong> {paymentMethod}</p>
                </div>

                <div className="confirm-info-block">
                  <h2>Order Items</h2>
                  {cartItems.length === 0 ? (
                    <Message>Your cart is empty</Message>
                  ) : (
                    <table className="confirm-items-table">
                      <thead><tr><th>Product</th><th>Qty</th><th>Price</th><th>Total</th></tr></thead>
                      <tbody>
                        {cartItems.map((item) => (
                          <tr key={getCartItemKey(item)}>
                            <td>
                              <div className="confirm-product-cell">
                                <img src={Array.isArray(item?.image) ? item?.image[0] : item?.image} alt="Product" />
                                {getCartItemLink(item) ? (
                                  <Link to={getCartItemLink(item)}>{item?.name}</Link>
                                ) : (
                                  <div>
                                    <span>{item?.name}</span>
                                    {item?.quoteSnapshot?.sizeLabel ? (
                                      <small>{item?.quoteSnapshot?.sizeLabel} / {item?.quoteSnapshot?.layers} Layers / Qty {item?.quoteSnapshot?.pcbQuantity}</small>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>{item?.quantity}</td>
                            <td>${formatUnitPrice(item?.price)}</td>
                            <td><strong>${(item?.quantity * item.price).toFixed(2)}</strong></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </section>

            <aside className="checkout-sidebar">
              <form onSubmit={submitHandler} className="checkout-summary-card">
                <h2>Summary</h2>
                <dl>
                  <div><dt>Items in cart:</dt><dd>{itemCount}</dd></div>
                  <div><dt>Subtotal:</dt><dd>${itemsPrice}</dd></div>
                  <div>
                    <dt>Shipping:</dt>
                    <dd className={isShippingEstimateFailed ? "checkout-summary-error" : ""}>
                      {isShippingEstimateFailed
                        ? shippingEstimateError || "Shipping estimate failed / Contact us"
                        : shippingQuoteOptions.length > 0
                          ? (
                            <select
                              className="checkout-shipping-select"
                              value={selectedShippingQuoteId}
                              onChange={handleShippingQuoteChange}
                              onMouseDown={handleShippingQuoteSelectExpand}
                              onKeyDown={handleShippingQuoteSelectExpand}
                              onBlur={handleShippingQuoteSelectCollapse}
                            >
                              {shippingQuoteOptions.map((option) => (
                                <option key={option.id} value={option.id}>
                                  {isShippingQuoteSelectExpanded || option.id !== selectedShippingQuoteId
                                    ? formatShippingQuoteOptionLabel(option)
                                    : formatShippingQuoteSelectedLabel(option)}
                                </option>
                              ))}
                            </select>
                          )
                          : `$${Number(shippingPrice).toFixed(2)}`}
                    </dd>
                  </div>
                  {shippingPriceSource === "4px" && <div><dt>Shipping source:</dt><dd>Estimated by 4PX</dd></div>}
                  <div className="checkout-summary-total">
                    <dt>Total:</dt>
                    <dd>{isShippingEstimateFailed ? "Pending" : `$${totalPrice}`}</dd>
                  </div>
                </dl>
                <div className="confirm-sidebar-actions">
                  <button className="checkout-primary-button" aria-label="Checkout via PayPal" type="submit" disabled={isShippingEstimateFailed}>Checkout via PayPal</button>
                  <button type="button" onClick={() => setShowModal(true)} className="checkout-secondary-button" aria-label="Checkout via Wire Transfer" disabled={isShippingEstimateFailed}>Checkout via Wire Transfer</button>
                </div>
              </form>
              <div className="checkout-security-card">
                <FaShieldAlt aria-hidden="true" />
                <span>Your data will be encrypted and transmitted securely.</span>
              </div>
            </aside>
          </div>
        </div>

        {showModal && (
          <div className="checkout-modal-backdrop" id="my-modal" role="dialog" aria-modal="true" aria-labelledby="wire-transfer-title">
            <div className="checkout-modal">
              <h3 id="wire-transfer-title">Bank Transfer Instructions</h3>
              <p>You are one step away from completing your order. To complete the order process, transfer the cart amount to the following bank account. Total cart value: <strong>${totalPrice}</strong>.</p>
              <p>
                <strong>Beneficiary:</strong> Creatyea Technology (SZ) CO., Limited<br />
                <strong>Bank:</strong> CHINA EVERBRIGHT BANK SHENZHEN BRANCH<br />
                <strong>A/C code:</strong> 38911488000024600<br />
                <strong>Bank Address:</strong> CEB BUILDING NO.18 ZIZHU QI AVENUE, ZHUZILIN SI ROAD, FUTIAN DISTRICT, SHENZHEN, CHINA 518040<br />
                <strong>SWIFT:</strong> EVERCNBJSZ1
              </p>
              <p>Once the payment is made, send the payment confirmation details like date, reference number, screenshot etc to: <a href="mailto:hello@creatyea.com">hello@creatyea.com</a></p>
              <div className="checkout-modal-actions">
                <button type="button" onClick={submitHandler} className="checkout-primary-button" disabled={isLoading || isShippingEstimateFailed}>
                  {isLoading ? <i className="fas fa-spinner fa-pulse"></i> : "Confirm Order"}
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="checkout-modal-close">Close</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </>
  );
};

export default ConfirmOrder;
